import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, updateDoc, doc, where, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Need } from '../../types/need';
import { ClipboardList, Check, X, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirm } from '../../contexts/ConfirmContext';

const DEPARTMENTS = [
  { id: 'all', name: 'Tous les départements' },
  { id: 'Gestion de Culte', name: 'Gestion de Culte' },
  { id: 'Finance', name: 'Finance' },
  { id: 'ADN - Amis des Nouveaux', name: 'ADN - Amis des Nouveaux' },
  { id: 'Académie', name: 'Académie' },
  { id: 'Sainte Cène', name: 'Sainte Cène' },
  { id: 'Communication & Sono', name: 'Communication & Sono' }
];

const STATUS_FILTERS = [
  { id: 'all', name: 'Tous les besoins' },
  { id: 'pending', name: 'En attente' },
  { id: 'addressed', name: 'Traités' }
];

export default function NeedsManagement() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [needs, setNeeds] = useState<Need[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fetchNeeds = async () => {
    try {
      setLoading(true);
      let q = query(collection(db, 'needs'), orderBy('createdAt', 'desc'));
      
      if (selectedDepartment !== 'all') {
        q = query(collection(db, 'needs'), where('department', '==', selectedDepartment), orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      const needsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Need[];

      setNeeds(needsData);
    } catch (error) {
      console.error('Erreur lors du chargement des besoins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNeeds();
  }, [selectedDepartment]);

  const handleMarkAsAddressed = async (needId: string) => {
    try {
      const needRef = doc(db, 'needs', needId);
      await updateDoc(needRef, {
        isAddressed: true,
        addressedBy: user?.id,
        addressedAt: new Date(),
        updatedAt: new Date()
      });

      showNotification('Besoin marqué comme traité', 'Le besoin a été marqué comme traité avec succès.');
      fetchNeeds();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du besoin:', error);
      showNotification('Erreur', 'Une erreur est survenue lors de la mise à jour du besoin.');
    }
  };

  const handleMarkAsPending = async (needId: string) => {
    try {
      const needRef = doc(db, 'needs', needId);
      await updateDoc(needRef, {
        isAddressed: false,
        addressedBy: null,
        addressedAt: null,
        updatedAt: new Date()
      });

      showNotification('Besoin marqué comme en attente', 'Le besoin a été marqué comme en attente.');
      fetchNeeds();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du besoin:', error);
      showNotification('Erreur', 'Une erreur est survenue lors de la mise à jour du besoin.');
    }
  };

  const handleDelete = async (needId: string) => {
    const ok = await confirm({ title: 'Supprimer le besoin', description: 'Êtes-vous sûr de vouloir supprimer ce besoin ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;

    try {
      await deleteDoc(doc(db, 'needs', needId));
      showNotification('Besoin supprimé', 'Le besoin a été supprimé avec succès.');
      fetchNeeds();
    } catch (error) {
      console.error('Erreur lors de la suppression du besoin:', error);
      showNotification('Erreur', 'Une erreur est survenue lors de la suppression du besoin.');
    }
  };

  const filteredNeeds = needs.filter(need => {
    if (statusFilter === 'pending') return !need.isAddressed;
    if (statusFilter === 'addressed') return need.isAddressed;
    return true;
  });

  // Reinitialiser la selection quand la liste affichee change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedDepartment, statusFilter]);

  const toggleOne = (needId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(needId)) next.delete(needId);
      else next.add(needId);
      return next;
    });
  };

  const allVisibleSelected = filteredNeeds.length > 0 && filteredNeeds.every(n => selectedIds.has(n.id!));

  const toggleAll = () => {
    setSelectedIds(prev => {
      if (filteredNeeds.length > 0 && filteredNeeds.every(n => prev.has(n.id!))) {
        return new Set();
      }
      return new Set(filteredNeeds.map(n => n.id!));
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const ok = await confirm({
      title: 'Supprimer les besoins sélectionnés',
      description: `Êtes-vous sûr de vouloir supprimer ${ids.length} besoin${ids.length > 1 ? 's' : ''} ? Cette action est irréversible.`,
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      // writeBatch limite a 500 operations : on decoupe par lots
      for (let i = 0; i < ids.length; i += 500) {
        const batch = writeBatch(db);
        ids.slice(i, i + 500).forEach(id => batch.delete(doc(db, 'needs', id)));
        await batch.commit();
      }
      showNotification('Besoins supprimés', `${ids.length} besoin${ids.length > 1 ? 's ont' : ' a'} été supprimé${ids.length > 1 ? 's' : ''} avec succès.`);
      setSelectedIds(new Set());
      fetchNeeds();
    } catch (error) {
      console.error('Erreur lors de la suppression groupée des besoins:', error);
      showNotification('Erreur', 'Une erreur est survenue lors de la suppression groupée.');
    }
  };

  const pendingCount = needs.filter(need => !need.isAddressed).length;
  const addressedCount = needs.filter(need => need.isAddressed).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#00665C]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <ClipboardList className="mr-3 text-[#00665C]" />
            Gestion des Besoins
          </h1>
          <p className="text-gray-600 mt-1">
            Suivez et gérez les besoins de tous les départements
          </p>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-orange-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Besoins en attente</h3>
              <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-green-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Besoins traités</h3>
              <p className="text-2xl font-bold text-green-600">{addressedCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-[#00665C]/25">
          <div className="flex items-center">
            <ClipboardList className="h-8 w-8 text-[#00665C] mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total besoins</h3>
              <p className="text-2xl font-bold text-[#00665C]">{needs.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Département
            </label>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
            >
              {DEPARTMENTS.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
            >
              {STATUS_FILTERS.map(status => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Barre d'action groupée */}
      {selectedIds.size > 0 && (
        <div className="bg-[#00665C]/5 border border-[#00665C]/20 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <span className="text-sm font-medium text-[#004d45]">
            {selectedIds.size} besoin{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer la sélection
            </button>
          </div>
        </div>
      )}

      {/* Liste des besoins */}
      {filteredNeeds.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <ClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun besoin trouvé</h3>
          <p className="text-gray-600">
            {statusFilter === 'pending' ? 'Aucun besoin en attente.' : 
             statusFilter === 'addressed' ? 'Aucun besoin traité.' : 
             'Aucun besoin enregistré pour ce département.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Version mobile */}
          <div className="sm:hidden">
            {filteredNeeds.map((need) => (
              <div key={need.id} className="border-b border-gray-100 p-4">
                <div className="flex justify-between items-start mb-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(need.id!)}
                    onChange={() => toggleOne(need.id!)}
                    className="mt-1 mr-3 h-4 w-4 rounded border-gray-300 text-[#00665C] focus:ring-[#00665C]"
                  />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{need.description}</h3>
                    <p className="text-sm text-gray-500 mt-1">{need.department}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(need.reportDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 ml-2">
                    {need.isAddressed ? (
                      <>
                        <button
                          onClick={() => handleMarkAsPending(need.id!)}
                          className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                          title="Marquer comme en attente"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Traité
                        </span>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleMarkAsAddressed(need.id!)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Marquer comme traité"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          En attente
                        </span>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(need.id!)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Version desktop */}
          <div className="hidden sm:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-gray-300 text-[#00665C] focus:ring-[#00665C]"
                      title="Tout sélectionner"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Besoin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Département
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date du rapport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredNeeds.map((need) => (
                  <tr key={need.id} className={`hover:bg-gray-50 ${selectedIds.has(need.id!) ? 'bg-[#00665C]/5' : ''}`}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(need.id!)}
                        onChange={() => toggleOne(need.id!)}
                        className="h-4 w-4 rounded border-gray-300 text-[#00665C] focus:ring-[#00665C]"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{need.description}</p>
                      <p className="text-xs text-gray-500">
                        Rapport du {new Date(need.reportDate).toLocaleDateString('fr-FR')}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#00665C]/15 text-[#004d45]">
                        {need.department}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(need.reportDate).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        need.isAddressed 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {need.isAddressed ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Traité
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-3 w-3 mr-1" />
                            En attente
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {need.isAddressed ? (
                          <button
                            onClick={() => handleMarkAsPending(need.id!)}
                            className="text-orange-600 hover:text-orange-900 p-1 hover:bg-orange-50 rounded"
                            title="Marquer comme en attente"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMarkAsAddressed(need.id!)}
                            className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                            title="Marquer comme traité"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(need.id!)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}